import React, { memo } from 'react'
import { usePageWidth, LayoutBand, FlowGrid, colors, Card, Skeleton, Bleed, Row } from '@fs/zion-ui'
import { css } from '@emotion/core'
import GroupListHeader from './GroupListHeader'
import GroupCardContents from './GroupCardContents'
import GroupCardContentsPending from './GroupCardContentsPending'
import GroupCardContentsFailed from './GroupCardContentsFailed'
import SkeletonAnimationSyncDecorator from '../SkeletonAnimationSyncDecorator'

const wrapCss = css`
  height: 100%;
  display: flex;
  flex-flow: column;
`
/**
 *
 * @param {{refetchGroupList: () => Promise<void>
 * totalGroupCount: number | undefined
 * groups: import('@fs/zion-tree-types').GroupSummary[]
 * isResearcherGroupList?: boolean }} param0
 * @returns {JSX.Element}
 */

function GroupList({ refetchGroupList, totalGroupCount, groups, isResearcherGroupList }) {
  const atPageWidth = usePageWidth()
  const isMobile = atPageWidth({ lg: false, default: true })

  return (
    <div css={wrapCss} data-testid={isResearcherGroupList ? 'research-tree-page' : 'family-groups-page'}>
      <LayoutBand marginY="xs" color={colors.gray00}>
        <GroupListHeader
          isResearcherGroupList={isResearcherGroupList}
          totalGroupCount={totalGroupCount}
          refetchGroupList={refetchGroupList}
        />
      </LayoutBand>
      <LayoutBand marginY="xs" growY>
        <FlowGrid gutters="xs" columnCount={isMobile ? 1 : 2}>
          {/* FlowGrid requires that the card is a direct child, it can't be in a separate component. */}
          {groups?.map((group, index) => {
            let Component = GroupCardContents
            const cardProps = {}

            if (group.memberStatus === 'PENDING') {
              cardProps.backgroundColor = colors.blue03
              Component = GroupCardContentsPending
            } else if (group.isFailed && !group.isProcessing) {
              cardProps.backgroundColor = colors.yellow03
              Component = GroupCardContentsFailed
            } else if (group.id) {
              cardProps.to = `/${isResearcherGroupList ? 'research-tree' : 'family'}/${group.id}`
            }
            if (group.isProcessing) {
              cardProps.backgroundColor = colors.blue02
            }
            const cardKey = group.id || `list-card-${index}`
            return (
              <Card {...cardProps} key={cardKey} data-testid="group-list-item" margins="xs">
                <Component group={group} refetchGroupList={refetchGroupList} />
              </Card>
            )
          })}
        </FlowGrid>
      </LayoutBand>
    </div>
  )
}

export default memo(GroupList)

export function GroupListSkeleton() {
  const atPageWidth = usePageWidth()
  const isMobile = atPageWidth({ lg: false, default: true })
  return (
    <SkeletonAnimationSyncDecorator>
      <LayoutBand marginY="xs" color={colors.gray00}>
        <Skeleton.H2 />
        <Skeleton.Paragraph lineCount={2} />
        {/* eslint-disable-next-line test-selectors/button -- loading skeleton */}
        <Skeleton.Button minWidth={160} />
      </LayoutBand>
      <LayoutBand growY marginY="xs">
        <FlowGrid gutters="xs" columnCount={isMobile ? 1 : 2} orientation="vertical">
          {new Array(4).fill().map((d, i) => {
            return (
              // eslint-disable-next-line react/no-array-index-key -- loading skeleton
              <Card key={i} margins="xs">
                <Bleed top start bottom>
                  <Row>
                    <Skeleton.Image width={121} height={91} />
                    <div growX>
                      <Skeleton.H4 />
                      <Skeleton.Chip />
                    </div>
                  </Row>
                </Bleed>
              </Card>
            )
          })}
        </FlowGrid>
      </LayoutBand>
    </SkeletonAnimationSyncDecorator>
  )
}
